import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  Fingerprint, IdCard as IdCardIcon, Vote, CreditCard,
  Upload, Download, Trash2, Loader2, Sparkles, IndianRupee,
  ArrowLeft, ShieldCheck, Clock, AlertTriangle, KeyRound,
  RefreshCw, Focus, Wand2, Printer,
} from "lucide-react";

const CHARGE_INR = 5;

const CARD_TYPES = [
  {
    key: "aadhaar",
    label: "Aadhaar Card",
    icon: Fingerprint,
    tint: "brand-blue",
    accept: "application/pdf",
    needsPassword: true,
    hint: "Official UIDAI PDF (locked or unlocked)",
  },
  {
    key: "pan",
    label: "PAN Card",
    icon: IdCardIcon,
    tint: "brand-green",
    accept: "application/pdf",
    needsPassword: true,
    hint: "e-PAN PDF from NSDL / UTIITSL (password optional)",
  },
  {
    key: "voter",
    label: "Voter ID",
    icon: Vote,
    tint: "fuchsia-500",
    accept: "application/pdf,image/jpeg,image/png",
    needsPassword: false,
    hint: "PDF or JPG / PNG of the e-EPIC",
  },
  {
    key: "other",
    label: "Other Cards",
    icon: CreditCard,
    tint: "amber-500",
    needsPassword: false,
    accept: "application/pdf,image/jpeg,image/png",
    hint: "Any card — you'll draw the Front & Back manually",
  },
];

export default function PvcCutter() {
  const { user, refreshMe } = useAuth();
  const [selected, setSelected] = useState(null);   // one of CARD_TYPES or null
  const [job, setJob] = useState(null);             // active job payload
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSide, setManualSide] = useState("front");

  const goBack = () => { setSelected(null); setJob(null); };
  const jobId = job?.id;

  useEffect(() => () => {
    // best-effort cleanup on unmount — but NEVER delete a paid job the
    // retailer already downloaded, they may re-open the tab shortly and
    // expect their JPGs to still be there for a few more minutes.
    if (jobId && !job?.paid) {
      api.delete(`/pvc/${jobId}`).catch(() => {});
    }
   
  }, []);

  return (
    <div className="space-y-6 max-w-6xl" data-testid="pvc-cutter-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Print Studio</p>
          <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">PVC Card Cutter</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Upload the official Aadhaar / PAN / Voter / any card — MaaPrint
            auto-detects Front &amp; Back and gives you high-quality{" "}
            <b>600 DPI JPGs</b> ready for PVC printing. For Aadhaar, the two
            physical cards at the bottom of the UIDAI PDF are cleanly
            extracted; the Address Letter and INFORMATION sections above are
            ignored. ₹{CHARGE_INR} per job.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 text-right">
          <Badge variant="outline" className="bg-brand-green/10 text-brand-green border-brand-green/20">
            <ShieldCheck size={12} className="mr-1" />
            Files auto-delete in 15 min
          </Badge>
          <p className="text-[11px] text-muted-foreground">
            Balance: ₹{Number(user?.wallet_balance || 0).toFixed(2)}
          </p>
        </div>
      </header>

      {!selected && (
        <CardTypeGrid onSelect={setSelected} />
      )}

      {selected && !job && (
        <UploadPanel
          card={selected}
          onCancel={goBack}
          onJobCreated={(j) => setJob(j)}
        />
      )}

      {selected && job && (
        <JobPanel
          job={job}
          setJob={setJob}
          card={selected}
          onBack={goBack}
          onManualCrop={(side) => { setManualSide(side); setManualOpen(true); }}
          refreshMe={refreshMe}
        />
      )}

      {jobId && (
        <ManualCropDialog
          open={manualOpen}
          onCancel={() => setManualOpen(false)}
          side={manualSide}
          job={job}
          onDone={(nextJob) => { setJob(nextJob); setManualOpen(false); }}
        />
      )}
    </div>
  );
}

/* ---------- Card type grid ---------- */
function CardTypeGrid({ onSelect }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pvc-card-grid">
      {CARD_TYPES.map((c) => {
        const Icon = c.icon;
        return (
          <button
            key={c.key}
            onClick={() => onSelect(c)}
            className="group text-left rounded-xl border border-border bg-card p-6 hover:border-primary/60 hover:shadow-lg transition-all"
            data-testid={`pvc-card-${c.key}`}
          >
            <div className={`h-12 w-12 rounded-lg bg-${c.tint}/10 text-${c.tint} flex items-center justify-center`}>
              <Icon size={22} />
            </div>
            <h3 className="font-display font-semibold mt-4">{c.label}</h3>
            <p className="text-xs text-muted-foreground mt-1 leading-snug">{c.hint}</p>
            <p className="text-[11px] mt-3 flex items-center gap-1 text-primary">
              <Wand2 size={11} />
              {c.key === "aadhaar" ? "Auto-cuts the 2 cards from UIDAI PDF"
               : c.key === "pan"     ? "Auto-detects Front / Back"
               : c.key === "voter"   ? "Auto-detects Front / Back"
               : "Manual Front / Back crop"}
            </p>
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Upload panel ---------- */
function UploadPanel({ card, onCancel, onJobCreated }) {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const Icon = card.icon;
  // While the auth context is briefly `null` (e.g. token-refresh window) don't
  // flash a low-balance alert or disable the button — that would UX-jitter.
  const lowBalance = user != null && Number(user.wallet_balance || 0) < CHARGE_INR;

  const submit = async () => {
    if (!file) return toast.error("Choose a file first");
    // Password is OPTIONAL — the backend accepts unprotected PDFs too. If the
    // PDF is actually encrypted and the retailer left the field blank the
    // backend returns 400 "Wrong password — please try again" and we surface
    // that error clearly. This lets retailers upload already-unlocked mAadhaar
    // PDFs without typing a dummy password.
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("card_type", card.key);
      if (password) fd.append("password", password);
      const { data } = await api.post("/pvc/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Toast tone depends on what the retailer needs to do next:
      //   • Success → "Detected Front & Back"
      //   • Other → warn (manual crop coming next)
      //   • Aadhaar/PAN/Voter with manual_needed → error (must re-upload)
      if (!data.manual_needed) {
        toast.success("Detected Front & Back automatically");
      } else if (card.key === "other") {
        toast.info("Uploaded — please crop Front & Back manually");
      } else {
        toast.error("Auto-detect failed — please re-upload the official UIDAI PDF");
      }
      onJobCreated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onCancel} className="mb-1" data-testid="pvc-back-btn">
        <ArrowLeft size={13} className="mr-1" /> Choose another card
      </Button>
      <Card className="p-6 border-border bg-card space-y-5" data-testid="pvc-upload-panel">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-lg bg-${card.tint}/10 text-${card.tint} flex items-center justify-center`}>
            <Icon size={20} />
          </div>
          <div>
            <h3 className="font-display font-semibold">{card.label}</h3>
            <p className="text-xs text-muted-foreground">{card.hint}</p>
          </div>
        </div>

        <label
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            const f = e.dataTransfer.files?.[0];
            if (f) setFile(f);
          }}
          className={`block cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition
            ${drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-secondary/40"}`}
          data-testid="pvc-dropzone"
        >
          <div className="h-14 w-14 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto">
            <Upload size={22} />
          </div>
          <p className="mt-3 font-medium">Drop your file here or click to browse</p>
          <p className="text-xs text-muted-foreground mt-1">
            {card.accept.includes("pdf") ? "PDF" : ""}
            {card.accept.includes("image") ? " · JPG · PNG" : ""}
            {" — up to 15 MB"}
          </p>
          {file && (
            <p className="text-xs mt-3 font-mono bg-secondary rounded px-2 py-1 inline-block">
              {file.name} · {(file.size / 1024).toFixed(1)} KB
            </p>
          )}
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept={card.accept}
            onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])}
            data-testid="pvc-file-input"
          />
        </label>

        {card.needsPassword && (
          <div>
            <Label className="flex items-center gap-1.5 text-xs">
              <KeyRound size={12} /> PDF password <span className="text-muted-foreground font-normal">· optional</span>
            </Label>
            <Input
              type="password"
              placeholder={card.key === "aadhaar" ? "Leave blank if the PDF is not locked" : "Leave blank if the PDF is not locked"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5"
              data-testid="pvc-password-input"
              autoComplete="off"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              {card.key === "aadhaar"
                ? "For locked mAadhaar PDFs: first 4 letters of name (CAPS) + year of birth. Not stored — auto-deleted with your files."
                : "Password given when you downloaded the e-PAN. Leave blank if you already unlocked it. Not stored on our servers."}
            </p>
          </div>
        )}

        <Alert className="border-brand-blue/30 bg-brand-blue/5">
          <ShieldCheck className="h-4 w-4 text-brand-blue" />
          <AlertDescription className="text-xs">
            ₹{CHARGE_INR} will be debited from your wallet only after you click{" "}
            <b>Generate cards</b> on the preview screen. Files auto-delete in 15 minutes.
          </AlertDescription>
        </Alert>

        {lowBalance && (
          <Alert className="border-destructive/40 bg-destructive/5" data-testid="pvc-low-balance-alert">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <AlertDescription className="text-xs">
              Your wallet balance (₹{Number(user?.wallet_balance || 0).toFixed(2)}) is below ₹{CHARGE_INR}. Please recharge before uploading — the job will be rejected on submit.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onCancel} data-testid="pvc-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !file || lowBalance}
            className="bg-primary hover:bg-brand-blue-hover"
            data-testid="pvc-upload-btn"
          >
            {busy
              ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Processing…</>
              : <><Sparkles size={14} className="mr-1.5" /> Detect Front &amp; Back</>}
          </Button>
        </div>
      </Card>
    </div>
  );
}

/* ---------- Preview / Download panel ---------- */
function JobPanel({ job, setJob, card, onBack, onManualCrop, refreshMe }) {
  const [busy, setBusy] = useState(false);
  const [ttlLeftSec, setTtlLeftSec] = useState(job.ttl_minutes * 60);
  const paid = job.paid || false;

  // Live countdown
  useEffect(() => {
    const exp = new Date(job.expires_at).getTime();
    const t = setInterval(() => {
      const left = Math.max(0, Math.floor((exp - Date.now()) / 1000));
      setTtlLeftSec(left);
      if (left <= 0) clearInterval(t);
    }, 1000);
    return () => clearInterval(t);
  }, [job.expires_at]);

  const previewUrl = (side) => `${API_BASE}/pvc/${job.id}/preview/${side}?v=${job._v || 0}`;

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pvc/${job.id}/generate`);
      setJob({ ...job, paid: true, front_url: data.front_url, back_url: data.back_url });
      toast.success(`₹${CHARGE_INR} debited · JPGs ready`);
      await refreshMe();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const download = async (side) => {
    try {
      const resp = await api.get(`/pvc/${job.id}/download/${side}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `MaaPrint_${card.label.replace(/\s+/g, "_")}_${side}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      setJob({ ...job, paid_downloaded: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const directPrint = async () => {
    if (!paid) return;
    try {
      // Fetch both side JPGs and inline them into a hidden iframe with
      // exact CR80 physical dimensions (85.6 × 54 mm). Browser's native
      // print dialog fires so the retailer prints without Connector.
      const [frontResp, backResp] = await Promise.all([
        api.get(`/pvc/${job.id}/download/front`, { responseType: "blob" }),
        api.get(`/pvc/${job.id}/download/back`, { responseType: "blob" }),
      ]);
      const frontUrl = URL.createObjectURL(frontResp.data);
      const backUrl = URL.createObjectURL(backResp.data);
      const iframe = document.createElement("iframe");
      Object.assign(iframe.style, {
        position: "fixed", right: "0", bottom: "0",
        width: "0", height: "0", border: "0",
      });
      document.body.appendChild(iframe);
      const doc = iframe.contentDocument;
      doc.open();
      doc.write(`
        <html><head><title>MaaPrint PVC · ${card.label}</title>
          <style>
            @page { size: A4; margin: 10mm; }
            body { margin: 0; font-family: system-ui, sans-serif; }
            .sheet { display: flex; gap: 8mm; flex-wrap: wrap; margin-top: 15mm; }
            .card { width: 85.6mm; height: 54mm; overflow: hidden; box-shadow: 0 0 0 0.2mm #999; }
            .card img { width: 100%; height: 100%; object-fit: cover; display: block; }
            .hint { font-size: 10pt; color: #555; margin: 0 0 6mm; }
          </style>
        </head><body>
          <div class="hint">MaaPrint · ${card.label} · CR80 85.6 × 54 mm @ 600 DPI</div>
          <div class="sheet">
            <div class="card"><img src="${frontUrl}" /></div>
            <div class="card"><img src="${backUrl}" /></div>
          </div>
        </body></html>`);
      doc.close();
      iframe.onload = () => setTimeout(() => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
        catch { toast.error("Browser blocked the print dialog"); }
      }, 400);
      setTimeout(() => {
        URL.revokeObjectURL(frontUrl);
        URL.revokeObjectURL(backUrl);
        try { document.body.removeChild(iframe); } catch { /* ok */ }
      }, 60000);
      setJob({ ...job, paid_downloaded: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const deleteNow = async () => {
    try {
      await api.delete(`/pvc/${job.id}`);
      toast.success("All files deleted from server");
      onBack();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const mmss = useMemo(() => {
    const m = Math.floor(ttlLeftSec / 60);
    const s = ttlLeftSec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  }, [ttlLeftSec]);

  const bothReady = job.front && job.back;

  return (
    <div className="space-y-5" data-testid="pvc-job-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="pvc-job-back-btn">
            <ArrowLeft size={13} className="mr-1" /> Change card
          </Button>
          <Badge variant="outline">{card.label}</Badge>
        </div>
        <Badge variant="outline" className={ttlLeftSec < 120 ? "bg-amber-500/10 text-amber-700 border-amber-500/30" : ""} data-testid="pvc-ttl-badge">
          <Clock size={12} className="mr-1" />
          Auto-delete in {mmss}
        </Badge>
      </div>

      {job.manual_needed && !bothReady && card.key === "other" && (
        <Alert className="border-amber-500/30 bg-amber-500/5">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-xs">
            Please draw the Front &amp; Back regions manually — tap the <b>Manual crop</b> button on each side below.
          </AlertDescription>
        </Alert>
      )}

      {job.manual_needed && !bothReady && card.key !== "other" && (
        <Alert className="border-destructive/40 bg-destructive/5" data-testid="pvc-autodetect-failed">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          <AlertDescription className="text-xs">
            <b>Couldn&apos;t auto-detect Front &amp; Back.</b> Please make sure the
            uploaded PDF is the official {card.label} download (both sides
            included) and try again. Tap <b>Change card</b> to re-upload.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        <SidePreview
          side="front" label="Front"
          job={job} previewUrl={previewUrl}
          allowManual={true}
          onManualCrop={() => onManualCrop("front")}
          onDownload={paid ? () => download("front") : null}
        />
        <SidePreview
          side="back" label="Back"
          job={job} previewUrl={previewUrl}
          allowManual={true}
          onManualCrop={() => onManualCrop("back")}
          onDownload={paid ? () => download("back") : null}
        />
      </div>

      <Card className="p-4 border-border bg-card flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-muted-foreground max-w-md">
          {card.key === "aadhaar"
            ? "Front + Back JPGs are the two Aadhaar cards from the bottom of the UIDAI PDF (Address Letter and INFORMATION sections excluded). ₹" + CHARGE_INR + " charged once for both sides."
            : card.key === "other"
              ? "Files are 600 DPI JPGs at 85.6 × 54 mm PVC card size. ₹" + CHARGE_INR + " charged once for both sides."
              : "Files are high-quality 600 DPI JPGs. ₹" + CHARGE_INR + " charged once for both sides."}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={deleteNow} className="text-destructive hover:text-destructive" data-testid="pvc-delete-now-btn">
            <Trash2 size={13} className="mr-1.5" /> Delete Now
          </Button>
          {!paid ? (
            <Button
              onClick={generate}
              disabled={busy || !bothReady}
              className="bg-brand-green hover:bg-brand-green-hover text-white"
              data-testid="pvc-generate-btn"
            >
              {busy
                ? <><Loader2 size={13} className="mr-1.5 animate-spin" /> Charging wallet…</>
                : <><IndianRupee size={13} className="mr-0.5" />{CHARGE_INR} · Generate cards</>}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={directPrint} data-testid="pvc-direct-print-btn">
                <Printer size={13} className="mr-1.5" /> Direct Print
              </Button>
              <Badge variant="outline" className="bg-brand-green/10 text-brand-green border-brand-green/30 h-9 px-3 flex items-center">
                <ShieldCheck size={13} className="mr-1.5" /> Paid — ready to download
              </Badge>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

function SidePreview({ side, label, job, previewUrl, onManualCrop, onDownload, allowManual }) {
  const ready = job[side];
  return (
    <Card className="p-4 border-border bg-card space-y-3" data-testid={`pvc-side-${side}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded bg-primary/10 text-primary flex items-center justify-center">
            <CreditCard size={14} />
          </div>
          <div>
            <p className="font-medium text-sm">{label}</p>
            <p className="text-[11px] text-muted-foreground">600 DPI · High quality</p>
          </div>
        </div>
        {ready ? (
          <Badge variant="outline" className="bg-brand-green/10 text-brand-green border-brand-green/30">Ready</Badge>
        ) : (
          <Badge variant="outline">Not ready</Badge>
        )}
      </div>

      <div className="aspect-[85.6/54] rounded-md border border-border bg-[repeating-linear-gradient(45deg,#eee_0,#eee_10px,#f7f7f7_10px,#f7f7f7_20px)] dark:bg-neutral-900 flex items-center justify-center overflow-hidden">
        {ready ? (
          <img
            src={previewUrl(side)}
            alt={`${label} preview`}
            className="w-full h-full object-contain"
            data-testid={`pvc-preview-${side}`}
          />
        ) : (
          <div className="text-center p-4">
            <Focus size={20} className="mx-auto text-muted-foreground" />
            <p className="text-xs text-muted-foreground mt-2">
              {allowManual ? "Not detected — use manual crop" : "Not detected — please re-upload"}
            </p>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        {allowManual && (
          <Button variant="outline" size="sm" onClick={onManualCrop} className="flex-1" data-testid={`pvc-manual-${side}`}>
            <RefreshCw size={12} className="mr-1.5" /> {ready ? "Re-crop" : "Manual crop"}
          </Button>
        )}
        {onDownload && (
          <Button size="sm" onClick={onDownload} className={`${allowManual ? "flex-1" : "w-full"} bg-primary hover:bg-brand-blue-hover`} data-testid={`pvc-download-${side}`}>
            <Download size={12} className="mr-1.5" /> Download
          </Button>
        )}
      </div>
    </Card>
  );
}

/* ---------- Manual crop dialog (quad overlay) ---------- */
function ManualCropDialog({ open, onCancel, side, job, onDone }) {
  const [pageIndex, setPageIndex] = useState(0);
  const [corners, setCorners] = useState([
    [0.10, 0.10], [0.90, 0.10], [0.90, 0.90], [0.10, 0.90],
  ]);
  const [dragIdx, setDragIdx] = useState(-1);
  const [pageUrl, setPageUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [box, setBox] = useState({ w: 1, h: 1, offX: 0, offY: 0 });
  const [imgDims, setImgDims] = useState({ w: 0, h: 0 });
  const holderRef = useRef(null);
  const pageBlobRef = useRef(null);

  useEffect(() => {
    if (!open || !job?.id) return;
    setPageIndex(job.card_type === "other" && side === "back" && (job.page_count || 1) > 1 ? 1 : 0);
    setCorners([[0.10, 0.10], [0.90, 0.10], [0.90, 0.90], [0.10, 0.90]]);
  }, [open, side, job]);

  useEffect(() => {
    if (!open || !job?.id) return;
    (async () => {
      try {
        const resp = await api.get(`/pvc/${job.id}/page/${pageIndex}`, { responseType: "blob" });
        if (pageBlobRef.current) URL.revokeObjectURL(pageBlobRef.current);
        const url = URL.createObjectURL(resp.data);
        pageBlobRef.current = url;
        setPageUrl(url);
      } catch (e) {
         
        console.error("failed to load page preview", e);
      }
    })();
    return () => {
      if (pageBlobRef.current) URL.revokeObjectURL(pageBlobRef.current);
      pageBlobRef.current = null;
    };
  }, [open, job?.id, pageIndex]);

  const recomputeBox = () => {
    const el = holderRef.current;
    if (!el || !imgDims.w) return;
    const rect = el.getBoundingClientRect();
    const ratio = imgDims.w / imgDims.h;
    let w = rect.width;
    let h = w / ratio;
    if (h > rect.height) {
      h = rect.height;
      w = h * ratio;
    }
    setBox({ w, h, offX: (rect.width - w) / 2, offY: (rect.height - h) / 2 });
  };
  useEffect(() => {
    recomputeBox();
    const on = () => recomputeBox();
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
     
  }, [imgDims]);

  const toScreen = (pt) => ({ x: box.offX + pt[0] * box.w, y: box.offY + pt[1] * box.h });
  const toNorm = (px, py) => ([
    Math.max(0, Math.min(1, (px - box.offX) / box.w)),
    Math.max(0, Math.min(1, (py - box.offY) / box.h)),
  ]);

  const onPointerMove = (e) => {
    if (dragIdx < 0) return;
    const r = holderRef.current.getBoundingClientRect();
    const [nx, ny] = toNorm(e.clientX - r.left, e.clientY - r.top);
    setCorners((c) => c.map((v, i) => i === dragIdx ? [nx, ny] : v));
  };

  const apply = async () => {
    setBusy(true);
    try {
      const body = side === "front"
        ? { front_corners: corners, front_page: pageIndex }
        : { back_corners: corners, back_page: pageIndex };
      await api.post(`/pvc/${job.id}/manual-crop`, body);
      // Fetch the fresh job state — bump _v to bust preview cache.
      const updatedJob = {
        ...job,
        [side]: { preview_url: `/api/pvc/${job.id}/preview/${side}` },
        _v: (job._v || 0) + 1,
      };
      // Also mark manual_needed = false once BOTH sides exist.
      if (updatedJob.front && updatedJob.back) updatedJob.manual_needed = false;
      toast.success(`${side === "front" ? "Front" : "Back"} cropped`);
      onDone(updatedJob);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const svgPath = useMemo(() => {
    if (!corners) return "";
    const p = corners.map(toScreen);
    return `M ${p[0].x},${p[0].y} L ${p[1].x},${p[1].y} L ${p[2].x},${p[2].y} L ${p[3].x},${p[3].y} Z`;
     
  }, [corners, box]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden max-h-[92vh] flex flex-col" data-testid="pvc-manual-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <DialogTitle className="font-display flex items-center gap-2">
            <Focus size={18} className="text-primary" />
            Manually crop the {side === "front" ? "Front" : "Back"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Drag the 4 blue corners to the edges of the card — the region inside will be warped to a 85.6 × 54 mm PVC size at 600 DPI. Use <b>Rotate 90°</b> if the card is printed sideways.
          </DialogDescription>
        </DialogHeader>

        {job?.page_count > 1 && (
          <div className="px-6 pt-3 flex gap-2 items-center border-b pb-2">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Page</span>
            {Array.from({ length: Math.min(job.page_count, 4) }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPageIndex(i)}
                className={`h-7 w-7 rounded border text-xs
                  ${pageIndex === i ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}
                data-testid={`pvc-manual-page-${i}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 relative bg-neutral-900 min-h-[380px]" data-testid="pvc-manual-canvas">
          <div
            ref={holderRef}
            className="absolute inset-0 flex items-center justify-center touch-none"
            onPointerMove={onPointerMove}
            onPointerUp={(e) => { setDragIdx(-1); try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* */ } }}
            onPointerCancel={() => setDragIdx(-1)}
          >
            {pageUrl ? (
              <img
                src={pageUrl}
                alt="page"
                className="max-w-full max-h-full object-contain select-none pointer-events-none"
                onLoad={(e) => setImgDims({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
                draggable={false}
              />
            ) : (
              <Loader2 size={20} className="animate-spin text-white" />
            )}
            {box.w > 0 && (
              <svg className="absolute inset-0 w-full h-full pointer-events-none">
                <path d={svgPath} fill="rgba(15,98,254,0.18)" stroke="#0f62fe" strokeWidth="2" />
              </svg>
            )}
            {corners.map((c, i) => {
              const p = toScreen(c);
              return (
                <div
                  key={i}
                  onPointerDown={(e) => { e.preventDefault(); setDragIdx(i); e.currentTarget.setPointerCapture(e.pointerId); }}
                  className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-lg cursor-grab active:cursor-grabbing touch-none"
                  style={{ left: p.x, top: p.y, width: 26, height: 26, background: dragIdx === i ? "#22c55e" : "#0f62fe" }}
                  data-testid={`pvc-manual-corner-${i}`}
                />
              );
            })}
          </div>
        </div>

        <DialogFooter className="px-6 py-3 gap-2 border-t bg-secondary/30">
          <Button variant="outline" onClick={onCancel} data-testid="pvc-manual-cancel">Cancel</Button>
          <Button variant="outline" onClick={() => {
            // Rotate corner order 90° clockwise: [TL,TR,BR,BL] → [BL,TL,TR,BR]
            setCorners((c) => [c[3], c[0], c[1], c[2]]);
          }} data-testid="pvc-manual-rotate">
            <RefreshCw size={13} className="mr-1.5" /> Rotate 90°
          </Button>
          <Button variant="outline" onClick={() => {
            // Reset to full page (useful when the card fills the page).
            setCorners([[0.02, 0.02], [0.98, 0.02], [0.98, 0.98], [0.02, 0.98]]);
          }} data-testid="pvc-manual-reset">Reset</Button>
          <Button onClick={apply} disabled={busy}
            className="bg-brand-green hover:bg-brand-green-hover text-white"
            data-testid="pvc-manual-apply">
            {busy
              ? <><Loader2 size={13} className="mr-1.5 animate-spin" /> Cropping…</>
              : <><Sparkles size={13} className="mr-1.5" /> Apply crop</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
